import React, { useState, useEffect } from 'react';
import {
	Flex,
	Box,
	Container,
	Heading,
	Text,
	HStack,
	Badge,
	Button,
	Spinner,
	Center,
	Card,
	CardBody,
	Icon,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	IconButton,
} from '@chakra-ui/react';
import Navbar from '../../component/navbar';
import Footer from '../../component/footer';
import { ViewIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { MdShoppingBag } from 'react-icons/md';
import { getAllOrders } from '../../redux/actions/orderActions';
import { connect } from 'react-redux';
import Alert from '../../component/alert';
import { useNavigate } from 'react-router-dom';

const GROUP_SIZE = 20;

const STATUS_TABS = [
	{ label: 'All', value: '' },
	{ label: 'Pending', value: 'Pending' },
	{ label: 'Processing', value: 'Processing' },
	{ label: 'Shipped', value: 'Shipped' },
	{ label: 'Completed', value: 'Completed' },
	{ label: 'Canceled', value: 'Canceled' },
];

const STATUS_COLORS = {
	Completed: 'green',
	Processing: 'yellow',
	Shipped: 'cyan',
	Pending: 'blue',
	Canceled: 'red',
};

const Order = ({ getAllOrders, orders, loading, count }) => {
	const [offset, setOffset] = useState(0);
	const [status, setStatus] = useState('');
	const navigate = useNavigate();

	useEffect(() => {
		getAllOrders(offset, status);
	}, [offset, status]);

	const handleStatusChange = (newStatus) => {
		setStatus(newStatus);
		setOffset(0);
	};

	const totalPages = Math.ceil((count || 0) / GROUP_SIZE);
	const currentPage = offset / GROUP_SIZE;

	const getPageNumbers = () => {
		const pages: number[] = [];
		for (let i = 0; i < totalPages; i++) {
			if (i === 0 || i === totalPages - 1 || Math.abs(i - currentPage) <= 1) {
				pages.push(i);
			}
		}
		const result: (number | string)[] = [];
		let prev = -1;
		for (const p of pages) {
			if (prev !== -1 && p - prev > 1) result.push('…');
			result.push(p);
			prev = p;
		}
		return result;
	};

	return (
		<Flex direction="column" minH="100vh" bg="gray.50">
			<Navbar />
			<Alert />
			<Box flex="1">
				<Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
					{/* Header */}
					<HStack mb={6} spacing={3}>
						<Flex
							w="40px" h="40px"
							borderRadius="lg"
							bg="blue.50"
							align="center"
							justify="center"
						>
							<Icon as={MdShoppingBag} boxSize={5} color="blue.500" />
						</Flex>
						<Box>
							<Heading size="lg" color="gray.800">My Orders</Heading>
							{!loading && count > 0 && (
								<Text fontSize="sm" color="gray.500">
									{count} order{count !== 1 ? 's' : ''}{status ? ` · ${status}` : ''}
								</Text>
							)}
						</Box>
					</HStack>

					{/* Status Filter Tabs */}
					<Card borderRadius="2xl" shadow="sm" mb={4}>
						<CardBody py={3}>
							<HStack spacing={2} overflowX="auto" flexWrap={{ base: 'nowrap', md: 'wrap' }}>
								{STATUS_TABS.map((tab) => (
									<Button
										key={tab.value}
										size="sm"
										borderRadius="full"
										variant={status === tab.value ? 'solid' : 'ghost'}
										colorScheme={status === tab.value ? 'blue' : 'gray'}
										onClick={() => handleStatusChange(tab.value)}
										flexShrink={0}
									>
										{tab.label}
									</Button>
								))}
							</HStack>
						</CardBody>
					</Card>

					{/* Orders Table */}
					{loading ? (
						<Center minH="40vh">
							<Spinner size="xl" thickness="4px" color="blue.500" />
						</Center>
					) : !orders || orders.length === 0 ? (
						<Card borderRadius="2xl" shadow="sm">
							<CardBody>
								<Center py={16} flexDirection="column" gap={4}>
									<Flex
										w="64px" h="64px"
										borderRadius="2xl"
										bg="gray.100"
										align="center"
										justify="center"
									>
										<Icon as={MdShoppingBag} boxSize={8} color="gray.400" />
									</Flex>
									<Text fontSize="lg" fontWeight="semibold" color="gray.600">
										{status ? `No ${status.toLowerCase()} orders` : 'No orders yet'}
									</Text>
									{status ? (
										<Button
											variant="ghost"
											colorScheme="blue"
											size="sm"
											onClick={() => handleStatusChange('')}
										>
											Clear filter
										</Button>
									) : (
										<Button
											colorScheme="blue"
											borderRadius="xl"
											onClick={() => navigate('/store')}
										>
											Browse Store
										</Button>
									)}
								</Center>
							</CardBody>
						</Card>
					) : (
						<Card borderRadius="2xl" shadow="sm" overflow="hidden">
							<Box overflowX="auto">
								<Table variant="simple">
									<Thead>
										<Tr bg="gray.50">
											<Th fontSize="xs" letterSpacing="wider" color="gray.500">Order #</Th>
											<Th fontSize="xs" letterSpacing="wider" color="gray.500">Date</Th>
											<Th fontSize="xs" letterSpacing="wider" color="gray.500">Status</Th>
											<Th fontSize="xs" letterSpacing="wider" color="gray.500" display={{ base: 'none', md: 'table-cell' }}>Ship To</Th>
											<Th></Th>
										</Tr>
									</Thead>
									<Tbody>
										{orders.map((order) => (
											<Tr
												key={order.order_id}
												onClick={() => navigate(`${order.order_id}`)}
												_hover={{ bg: 'blue.50', cursor: 'pointer' }}
												transition="background 0.15s"
											>
												<Td>
													<Text fontWeight="semibold" color="gray.800">
														#{order.order_id}
													</Text>
												</Td>
												<Td>
													<Text fontSize="sm" color="gray.600">
														{new Date(order.created_at).toLocaleDateString('en-US', {
															year: 'numeric',
															month: 'short',
															day: 'numeric',
														})}
													</Text>
												</Td>
												<Td>
													<Badge
														colorScheme={STATUS_COLORS[order.status] || 'gray'}
														variant="subtle"
														borderRadius="full"
														px={3}
														py={0.5}
														fontSize="xs"
													>
														{order.status || 'Pending'}
													</Badge>
												</Td>
												<Td display={{ base: 'none', md: 'table-cell' }}>
													<Text fontSize="sm" color="gray.600" maxW="200px" noOfLines={1}>
														{order.shipping_address || '—'}
													</Text>
												</Td>
												<Td>
													<Button
														size="sm"
														colorScheme="blue"
														variant="ghost"
														leftIcon={<ViewIcon />}
														borderRadius="lg"
														onClick={(e) => {
															e.stopPropagation();
															navigate(`${order.order_id}`);
														}}
													>
														View
													</Button>
												</Td>
											</Tr>
										))}
									</Tbody>
								</Table>
							</Box>

							{/* Pagination */}
							{totalPages > 1 && (
								<Box px={4} py={4} borderTop="1px solid" borderColor="gray.100">
									<HStack spacing={2} justify="space-between" flexWrap="wrap">
										<Text fontSize="sm" color="gray.400">
											Page {currentPage + 1} of {totalPages}
										</Text>
										<HStack spacing={1}>
											<IconButton
												aria-label="Previous page"
												icon={<ChevronLeftIcon />}
												size="sm"
												borderRadius="lg"
												variant="ghost"
												isDisabled={currentPage === 0}
												onClick={() => setOffset((currentPage - 1) * GROUP_SIZE)}
											/>
											{getPageNumbers().map((p, i) =>
												p === '…' ? (
													<Text key={`e-${i}`} color="gray.400" px={1}>…</Text>
												) : (
													<Button
														key={p}
														size="sm"
														borderRadius="lg"
														colorScheme={p === currentPage ? 'blue' : 'gray'}
														variant={p === currentPage ? 'solid' : 'ghost'}
														onClick={() => setOffset((p as number) * GROUP_SIZE)}
													>
														{(p as number) + 1}
													</Button>
												)
											)}
											<IconButton
												aria-label="Next page"
												icon={<ChevronRightIcon />}
												size="sm"
												borderRadius="lg"
												variant="ghost"
												isDisabled={currentPage >= totalPages - 1}
												onClick={() => setOffset((currentPage + 1) * GROUP_SIZE)}
											/>
										</HStack>
									</HStack>
								</Box>
							)}
						</Card>
					)}
				</Container>
			</Box>
			<Footer />
		</Flex>
	);
};

const mapStateToProps = (state) => ({
	orders: state.order.orders?.data ?? [],
	count: state.order.orders?.count ?? 0,
	loading: state.order.ordersloading,
});

export default connect(mapStateToProps, { getAllOrders })(Order);
